// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * DOM rendering + the app's outward API. main.ts owns the engine and wiring;
 * this module builds the shell and exposes typed update methods so the rest of
 * the app never touches the DOM directly.
 */
import { icon } from './icons.ts';
import { mountEventDrawer } from './eventlog.ts';
import { openModal, howItWorksHtml, privacyHtml, aboutHtml } from './modals.ts';
import { fmtBytes, fmtCount } from './format.ts';
import type { Direction, TransferRow } from './types.ts';
import type { Hello } from './session.ts';
import type { EngineStatus } from './sync.ts';

export interface AppCallbacks {
  onChooseFolder: () => void;
  onDropFolder: (item: DataTransferItem) => void;
  onChooseFiles: () => void;
  onDirectionChange: (d: Direction) => void;
  onNameChange: (name: string) => void;
  onCopyLink: () => void;
  onShareLink: () => void;
  onDisconnect: () => void;
}

const STATUS_TEXT: Record<EngineStatus | 'idle', string> = {
  idle: 'Not started',
  waiting: 'Waiting for the other device…',
  paired: 'Connected — confirm the safety words',
  syncing: 'Syncing…',
  insync: 'In sync',
  error: 'Connection problem',
};

export class AppUI {
  private transfers = new Map<string, HTMLElement>();
  private cb: AppCallbacks;
  private supportsDir = true;

  constructor(root: HTMLElement, cb: AppCallbacks) {
    this.cb = cb;
    root.innerHTML = this.shellHtml();
    this.wire();
    mountEventDrawer(document.getElementById('drawer-body') as HTMLElement);
  }

  private shellHtml(): string {
    return `
      <div class="app-shell">
        <header class="topbar">
          <div class="brand">
            <span class="brand-mark">${icon('folderSync')}</span>
            <span class="brand-name">Syncwell</span>
          </div>
          <nav class="topnav">
            <button type="button" class="nav-btn" id="nav-how">${icon('help')}<span>How it works</span></button>
            <button type="button" class="nav-btn" id="nav-privacy">${icon('shield')}<span>Privacy</span></button>
            <button type="button" class="nav-btn" id="nav-about">${icon('info')}<span>About</span></button>
            <button type="button" class="nav-btn" id="nav-log" aria-label="Event log">${icon('logDrawer')}<span>Log</span></button>
          </nav>
        </header>

        <button type="button" class="trust-banner" id="trust-banner">
          ${icon('lock')}
          <span>Runs entirely in your browser. Files travel <strong>directly between your devices</strong>, encrypted end-to-end — never uploaded.</span>
        </button>

        <main class="main-content">
          <section class="card pairing-card" id="pairing-card">
            <h1 class="card-title">Sync a folder between your devices</h1>
            <p class="card-sub">Open this link on your other device (or send it to whoever you’re syncing with), then choose a folder on each.</p>

            <div class="pair-grid">
              <div class="pair-left">
                <label class="field-label" for="link-input">Pairing link</label>
                <div class="link-row">
                  <input type="text" id="link-input" class="link-input" readonly />
                  <button type="button" class="btn btn-ghost" id="btn-copy" title="Copy link">${icon('copy')}</button>
                  <button type="button" class="btn btn-ghost" id="btn-share" title="Share link">${icon('share')}</button>
                </div>
                <div class="opts-row">
                  <label class="field-label" for="dir-select">Direction</label>
                  <select id="dir-select" class="select">
                    <option value="both">Two-way (both fold&shy;ers stay in step)</option>
                    <option value="send">Send only (push my changes out)</option>
                    <option value="receive">Receive only (pull changes in)</option>
                  </select>
                </div>
                <div class="opts-row">
                  <label class="field-label" for="name-input">This device’s name</label>
                  <input type="text" id="name-input" class="text-input" maxlength="40" placeholder="e.g. Laptop" />
                </div>
              </div>
              <div class="pair-right">
                <div class="qr-box" id="qr-box"></div>
                <span class="qr-cap">${icon('qr')} Scan on your other device</span>
              </div>
            </div>
          </section>

          <section class="card folder-card" id="folder-card">
            <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="Choose a folder to sync">
              <div class="dz-icon">${icon('folder')}</div>
              <div class="dz-main" id="dz-main">Choose a folder to sync</div>
              <div class="dz-sub" id="dz-sub">Your browser will ask permission for this one folder. Nothing else is read. Drag a folder here, or click to pick.</div>
              <div class="dz-fallback" id="dz-fallback" hidden>
                <span class="muted small">This browser can’t mirror a folder on disk.</span>
                <button type="button" class="btn btn-ghost btn-sm" id="btn-files">Pick files to send instead</button>
              </div>
            </div>
            <div class="folder-chosen" id="folder-chosen" hidden>
              <div class="fc-row">
                <span class="fc-icon">${icon('check')}</span>
                <span class="fc-label" id="fc-label">—</span>
                <button type="button" class="btn btn-ghost btn-sm" id="btn-refolder">Change</button>
              </div>
            </div>
          </section>

          <section class="card session-card" id="session-card" hidden>
            <div class="status-row">
              <span class="status-dot" id="status-dot" data-status="idle"></span>
              <span class="status-text" id="status-text">${STATUS_TEXT.idle}</span>
              <span class="peer-name" id="peer-name"></span>
              <button type="button" class="btn btn-danger btn-sm" id="btn-disconnect">${icon('power')}<span>Disconnect</span></button>
            </div>

            <div class="sas-box" id="sas-box" hidden>
              <div class="sas-head">${icon('shield')} Safety words — read aloud, they must match on both devices</div>
              <div class="sas-words" id="sas-words"></div>
            </div>

            <div class="extra-peer" id="extra-peer" hidden>${icon('users')} Another device tried to join. Syncwell pairs two devices only.</div>

            <div class="summary-row">
              <div class="summary-cell"><span class="sc-icon">${icon('folder')}</span><span id="local-sum">—</span><span class="sc-tag">this device</span></div>
              <div class="summary-cell"><span class="sc-icon">${icon('devices')}</span><span id="remote-sum">—</span><span class="sc-tag">other device</span></div>
            </div>

            <div class="xfer-wrap">
              <div class="xfer-head">Transfers</div>
              <div class="xfer-list" id="xfer-list"><div class="xfer-empty" id="xfer-empty">Nothing to transfer yet.</div></div>
            </div>
          </section>
        </main>

        <footer class="site-footer">
          <div class="footer-inner">
            Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
            · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
          </div>
        </footer>

        <aside class="drawer" id="drawer" hidden aria-label="Event log">
          <div id="drawer-body"></div>
        </aside>
      </div>`;
  }

  private wire(): void {
    const $ = (id: string) => document.getElementById(id) as HTMLElement;
    $('nav-how').addEventListener('click', () => openModal('How Syncwell works', howItWorksHtml()));
    $('nav-privacy').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));
    $('nav-about').addEventListener('click', () => openModal('About Syncwell', aboutHtml()));
    $('trust-banner').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));
    $('nav-log').addEventListener('click', () => this.toggleDrawer());

    $('btn-copy').addEventListener('click', () => this.cb.onCopyLink());
    $('btn-share').addEventListener('click', () => this.cb.onShareLink());
    $('btn-files').addEventListener('click', () => this.cb.onChooseFiles());
    $('btn-disconnect').addEventListener('click', () => this.cb.onDisconnect());

    const dir = document.getElementById('dir-select') as HTMLSelectElement;
    dir.addEventListener('change', () => this.cb.onDirectionChange(dir.value as Direction));
    const name = document.getElementById('name-input') as HTMLInputElement;
    name.addEventListener('change', () => this.cb.onNameChange(name.value.trim()));

    const dz = $('dropzone');
    const chooseIfSupported = () => (this.supportsDir ? this.cb.onChooseFolder() : this.cb.onChooseFiles());
    dz.addEventListener('click', chooseIfSupported);
    dz.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
        e.preventDefault();
        chooseIfSupported();
      }
    });
    $('btn-refolder').addEventListener('click', chooseIfSupported);

    dz.addEventListener('dragover', (e) => {
      e.preventDefault();
      dz.classList.add('drag');
    });
    dz.addEventListener('dragleave', () => dz.classList.remove('drag'));
    dz.addEventListener('drop', (e) => {
      e.preventDefault();
      dz.classList.remove('drag');
      const item = e.dataTransfer?.items?.[0];
      if (item) this.cb.onDropFolder(item);
    });

    // Drawer close (× and Escape).
    document.getElementById('drawer')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('#drawer-close')) this.closeDrawer();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeDrawer();
    });
  }

  private toggleDrawer(): void {
    const d = document.getElementById('drawer') as HTMLElement;
    if (d.hidden) {
      d.hidden = false;
      d.classList.add('open');
    } else {
      this.closeDrawer();
    }
  }
  private closeDrawer(): void {
    const d = document.getElementById('drawer') as HTMLElement;
    d.classList.remove('open');
    d.hidden = true;
  }

  // ── public update API ───────────────────────────────────────────────────
  setSupportsDirectory(ok: boolean): void {
    this.supportsDir = ok;
    (document.getElementById('dz-fallback') as HTMLElement).hidden = ok;
    if (!ok) {
      (document.getElementById('dz-main') as HTMLElement).textContent = 'Pick files to send';
      (document.getElementById('dz-sub') as HTMLElement).textContent =
        'Folder mirroring needs a Chromium browser. You can still send files and receive them as downloads.';
    }
  }

  setInviteLink(url: string): void {
    (document.getElementById('link-input') as HTMLInputElement).value = url;
  }

  setQr(dataUrl: string): void {
    const box = document.getElementById('qr-box') as HTMLElement;
    box.innerHTML = `<img src="${dataUrl}" alt="Pairing QR code" width="150" height="150" />`;
  }

  setDirection(d: Direction): void {
    (document.getElementById('dir-select') as HTMLSelectElement).value = d;
  }

  setName(name: string): void {
    (document.getElementById('name-input') as HTMLInputElement).value = name;
  }

  setSas(words: string[]): void {
    const box = document.getElementById('sas-box') as HTMLElement;
    const wrap = document.getElementById('sas-words') as HTMLElement;
    wrap.innerHTML = words.map((w) => `<span class="sas-word">${w}</span>`).join('');
    box.hidden = false;
  }

  setFolder(label: string): void {
    (document.getElementById('folder-card') as HTMLElement).classList.add('has-folder');
    (document.getElementById('dropzone') as HTMLElement).hidden = true;
    const chosen = document.getElementById('folder-chosen') as HTMLElement;
    chosen.hidden = false;
    (document.getElementById('fc-label') as HTMLElement).textContent = label;
    (document.getElementById('session-card') as HTMLElement).hidden = false;
  }

  resetFolderPicker(): void {
    (document.getElementById('dropzone') as HTMLElement).hidden = false;
    (document.getElementById('folder-chosen') as HTMLElement).hidden = true;
  }

  setStatus(status: EngineStatus | 'idle', peerName?: string): void {
    (document.getElementById('status-dot') as HTMLElement).dataset.status = status;
    (document.getElementById('status-text') as HTMLElement).textContent = STATUS_TEXT[status];
    (document.getElementById('peer-name') as HTMLElement).textContent = peerName ? `· ${peerName}` : '';
  }

  setPeer(hello: Hello | null): void {
    (document.getElementById('peer-name') as HTMLElement).textContent = hello ? `· ${hello.name || 'peer'}` : '';
  }

  showExtraPeer(): void {
    (document.getElementById('extra-peer') as HTMLElement).hidden = false;
  }

  setLocalSummary(files: number, bytes: number): void {
    (document.getElementById('local-sum') as HTMLElement).textContent = `${fmtCount(files)} · ${fmtBytes(bytes)}`;
  }
  setRemoteSummary(files: number, bytes: number): void {
    (document.getElementById('remote-sum') as HTMLElement).textContent = files ? `${fmtCount(files)} · ${fmtBytes(bytes)}` : '—';
  }

  upsertTransfer(row: TransferRow): void {
    const list = document.getElementById('xfer-list') as HTMLElement;
    const empty = document.getElementById('xfer-empty');
    if (empty) empty.remove();
    const key = `${row.dir}:${row.path}`;
    let el = this.transfers.get(key);
    if (!el) {
      el = document.createElement('div');
      el.className = 'xfer-row';
      el.innerHTML = `
        <span class="xfer-dir">${icon(row.dir === 'in' ? 'arrowIn' : 'arrowOut')}</span>
        <span class="xfer-name"></span>
        <span class="xfer-bar"><span class="xfer-fill"></span></span>
        <span class="xfer-label"></span>`;
      (el.querySelector('.xfer-name') as HTMLElement).textContent = row.path;
      list.prepend(el);
      this.transfers.set(key, el);
      // Cap visible rows.
      const rows = list.querySelectorAll('.xfer-row');
      if (rows.length > 60) {
        const last = rows[rows.length - 1];
        const k = [...this.transfers.entries()].find(([, v]) => v === last)?.[0];
        if (k) this.transfers.delete(k);
        last.remove();
      }
    }
    el.dataset.state = row.state;
    (el.querySelector('.xfer-fill') as HTMLElement).style.width = `${row.pct}%`;
    (el.querySelector('.xfer-label') as HTMLElement).textContent = row.label;
  }
}
